import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { GET_RECURRING_INVOICES } from '../graphql/query';
import { UPDATE_RECURRING_INVOICE } from '../graphql/mutation';
import { RecurringInvoice } from '../types/interfaces';
import TaskLineTypeFields, { taskLineYupSchema, emptyTaskLine, useDefaultLineType } from './TaskLineTypeFields';

interface RecurringInvoiceDetailDialogProps {
  invoice: RecurringInvoice | null;
  open: boolean;
  onClose: () => void;
}

const validationSchema = yup.object({
  customerName: yup.string().required('Required'),
  customerABN: yup.string().required('Required'),
  customerAddress: yup.string().required('Required'),
  customerEmail: yup.string().email('Invalid email').required('Required'),
  projectName: yup.string().required('Required'),
  projectDescription: yup.string().required('Required'),
  discount: yup.number().min(0).max(100).required(),
  taxRate: yup.number().min(0).max(100).required(),
  frequency: yup.string().oneOf(['Monthly', 'Quarterly', 'Annually']).required(),
  nextRunDate: yup.string().required('Required'),
  endDate: yup.string().nullable().optional(),
  tasks: yup.array().of(taskLineYupSchema).min(1, 'At least one task is required'),
});

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    display="block"
    sx={{ mb: 1, fontWeight: 600 }}
  >
    {children}
  </Typography>
);

const RecurringInvoiceDetailDialog: React.FC<RecurringInvoiceDetailDialogProps> = ({
  invoice,
  open,
  onClose,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [updateRecurringInvoice, { loading: saving }] = useMutation(UPDATE_RECURRING_INVOICE, {
    refetchQueries: [{ query: GET_RECURRING_INVOICES }],
  });

  useEffect(() => {
    if (!open) setIsEditing(false);
  }, [open]);

  const defaultLineType = useDefaultLineType();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customerName: invoice?.customerName ?? '',
      customerABN: invoice?.customerABN ?? '',
      customerAddress: invoice?.customerAddress ?? '',
      customerEmail: invoice?.customerEmail ?? '',
      projectName: invoice?.projectName ?? '',
      projectDescription: invoice?.projectDescription ?? '',
      discount: invoice?.discount ?? 0,
      taxRate: invoice ? invoice.taxRate * 100 : 10,
      frequency: invoice?.frequency ?? 'Monthly',
      nextRunDate: invoice?.nextRunDate ? invoice.nextRunDate.substring(0, 10) : '',
      endDate: invoice?.endDate ? invoice.endDate.substring(0, 10) : '',
      tasks:
        invoice?.tasks?.map((t) => ({
          name: t.name,
          description: t.description,
          lineType: t.lineType,
          duration: t.duration,
          amount: t.amount,
        })) ?? [emptyTaskLine(defaultLineType)],
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!invoice) return;
      try {
        await updateRecurringInvoice({
          variables: {
            id: invoice.id,
            input: {
              customerName: values.customerName,
              customerABN: values.customerABN,
              customerAddress: values.customerAddress,
              customerEmail: values.customerEmail,
              projectName: values.projectName,
              projectDescription: values.projectDescription,
              discount: values.discount,
              taxRate: values.taxRate / 100,
              frequency: values.frequency,
              nextRunDate: new Date(values.nextRunDate + 'T00:00:00Z').toISOString(),
              endDate: values.endDate
                ? new Date(values.endDate + 'T00:00:00Z').toISOString()
                : null,
              tasks: values.tasks,
            },
          },
        });
        enqueueSnackbar('Recurring invoice updated successfully', { variant: 'success' });
        setIsEditing(false);
      } catch (err) {
        enqueueSnackbar(
          err instanceof Error ? err.message : 'Failed to update recurring invoice',
          { variant: 'error' }
        );
      }
    },
  });

  const handleCancelEdit = () => {
    formik.resetForm();
    setIsEditing(false);
  };

  const handleAddTask = () => {
    formik.setFieldValue('tasks', [
      ...formik.values.tasks,
      emptyTaskLine(defaultLineType),
    ]);
  };

  const handleRemoveTask = (index: number) => {
    const tasks = [...formik.values.tasks];
    tasks.splice(index, 1);
    formik.setFieldValue('tasks', tasks);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU');
  };

  const renderViewMode = () => {
    if (!invoice) return null;
    return (
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid size={6}>
            <SectionLabel>Customer</SectionLabel>
            <Typography variant="body2" fontWeight={600}>
              {invoice.customerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ABN: {invoice.customerABN}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.customerAddress}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.customerEmail}
            </Typography>
          </Grid>

          <Grid size={6}>
            <SectionLabel>Project</SectionLabel>
            <Typography variant="body2" fontWeight={600}>
              {invoice.projectName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.projectDescription}
            </Typography>
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={4}>
            <SectionLabel>Frequency</SectionLabel>
            <Typography variant="body2">{invoice.frequency}</Typography>
          </Grid>
          <Grid size={4}>
            <SectionLabel>Next Run</SectionLabel>
            <Typography variant="body2">{formatDate(invoice.nextRunDate)}</Typography>
          </Grid>
          <Grid size={4}>
            <SectionLabel>End Date</SectionLabel>
            <Typography variant="body2">
              {invoice.endDate ? formatDate(invoice.endDate) : 'No end'}
            </Typography>
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={12}>
            <SectionLabel>Tasks</SectionLabel>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Hours</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.name}</TableCell>
                    <TableCell>{task.description}</TableCell>
                    <TableCell align="right">
                      {task.lineType === 'FIXED' ? '—' : task.duration}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={4}>
            <SectionLabel>Discount</SectionLabel>
            <Typography variant="body2">{invoice.discount}%</Typography>
          </Grid>
          <Grid size={4}>
            <SectionLabel>GST Rate</SectionLabel>
            <Typography variant="body2">{(invoice.taxRate * 100).toFixed(0)}%</Typography>
          </Grid>
        </Grid>
      </DialogContent>
    );
  };

  const renderEditMode = () => (
    <DialogContent dividers>
      <Grid container spacing={2}>
        <Grid size={12}>
          <SectionLabel>Customer</SectionLabel>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Customer Name"
            name="customerName"
            value={formik.values.customerName}
            onChange={formik.handleChange}
            error={formik.touched.customerName && Boolean(formik.errors.customerName)}
            helperText={formik.touched.customerName && formik.errors.customerName}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Customer ABN"
            name="customerABN"
            value={formik.values.customerABN}
            onChange={formik.handleChange}
            error={formik.touched.customerABN && Boolean(formik.errors.customerABN)}
            helperText={formik.touched.customerABN && formik.errors.customerABN}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Customer Address"
            name="customerAddress"
            multiline
            rows={2}
            value={formik.values.customerAddress}
            onChange={formik.handleChange}
            error={formik.touched.customerAddress && Boolean(formik.errors.customerAddress)}
            helperText={formik.touched.customerAddress && formik.errors.customerAddress}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Customer Email"
            name="customerEmail"
            type="email"
            value={formik.values.customerEmail}
            onChange={formik.handleChange}
            error={formik.touched.customerEmail && Boolean(formik.errors.customerEmail)}
            helperText={formik.touched.customerEmail && formik.errors.customerEmail}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid size={12}>
          <SectionLabel>Project</SectionLabel>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Project Name"
            name="projectName"
            value={formik.values.projectName}
            onChange={formik.handleChange}
            error={formik.touched.projectName && Boolean(formik.errors.projectName)}
            helperText={formik.touched.projectName && formik.errors.projectName}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Project Description"
            name="projectDescription"
            value={formik.values.projectDescription}
            onChange={formik.handleChange}
            error={formik.touched.projectDescription && Boolean(formik.errors.projectDescription)}
            helperText={formik.touched.projectDescription && formik.errors.projectDescription}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid size={12}>
          <SectionLabel>Schedule</SectionLabel>
        </Grid>
        <Grid size={4}>
          <TextField
            fullWidth
            label="Frequency"
            name="frequency"
            select
            value={formik.values.frequency}
            onChange={formik.handleChange}
            error={formik.touched.frequency && Boolean(formik.errors.frequency)}
            helperText={formik.touched.frequency && formik.errors.frequency}
          >
            <MenuItem value="Monthly">Monthly</MenuItem>
            <MenuItem value="Quarterly">Quarterly</MenuItem>
            <MenuItem value="Annually">Annually</MenuItem>
          </TextField>
        </Grid>
        <Grid size={4}>
          <TextField
            fullWidth
            label="Next Run Date"
            name="nextRunDate"
            type="date"
            value={formik.values.nextRunDate}
            onChange={formik.handleChange}
            error={formik.touched.nextRunDate && Boolean(formik.errors.nextRunDate)}
            helperText={formik.touched.nextRunDate && formik.errors.nextRunDate}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            fullWidth
            label="End Date (optional)"
            name="endDate"
            type="date"
            value={formik.values.endDate || ''}
            onChange={formik.handleChange}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid size={12}>
          <SectionLabel>Tasks</SectionLabel>
        </Grid>

        {formik.values.tasks.map((task, index) => (
          <React.Fragment key={index}>
            <Grid size={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Task {index + 1}</Typography>
                {index > 0 && (
                  <IconButton size="small" onClick={() => handleRemoveTask(index)}>
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Task Name"
                name={`tasks[${index}].name`}
                value={task.name}
                onChange={formik.handleChange}
                error={
                  formik.touched.tasks?.[index]?.name &&
                  Boolean((formik.errors.tasks?.[index] as any)?.name)
                }
                helperText={
                  formik.touched.tasks?.[index]?.name &&
                  (formik.errors.tasks?.[index] as any)?.name
                }
              />
            </Grid>
            <TaskLineTypeFields
                index={index}
                lineType={task.lineType ?? 'HOURLY'}
                duration={task.duration ?? 0}
                amount={task.amount ?? 0}
                onChange={formik.handleChange}
                touched={formik.touched.tasks?.[index]}
                errors={formik.errors.tasks?.[index] as any}
              />
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                name={`tasks[${index}].description`}
                value={task.description}
                onChange={formik.handleChange}
                error={
                  formik.touched.tasks?.[index]?.description &&
                  Boolean((formik.errors.tasks?.[index] as any)?.description)
                }
                helperText={
                  formik.touched.tasks?.[index]?.description &&
                  (formik.errors.tasks?.[index] as any)?.description
                }
              />
            </Grid>
          </React.Fragment>
        ))}

        <Grid size={12}>
          <Button startIcon={<AddIcon />} onClick={handleAddTask} variant="outlined">
            Add Task
          </Button>
        </Grid>

        <Grid size={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>
        <Grid size={12}>
          <SectionLabel>Financial</SectionLabel>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Discount (%)"
            name="discount"
            type="number"
            value={formik.values.discount}
            onChange={formik.handleChange}
            error={formik.touched.discount && Boolean(formik.errors.discount)}
            helperText={formik.touched.discount && formik.errors.discount}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Tax Rate / GST (%)"
            name="taxRate"
            type="number"
            value={formik.values.taxRate}
            onChange={formik.handleChange}
            error={formik.touched.taxRate && Boolean(formik.errors.taxRate)}
            helperText={formik.touched.taxRate && formik.errors.taxRate}
          />
        </Grid>
      </Grid>
    </DialogContent>
  );

  return (
    <Dialog
      open={open}
      onClose={isEditing ? undefined : onClose}
      disableEscapeKeyDown={isEditing}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h6" component="span">
            {invoice?.customerName ?? ''}
          </Typography>
          {invoice && (
            <Chip
              label={invoice.isActive ? 'Active' : 'Paused'}
              size="small"
              sx={{
                backgroundColor: invoice.isActive ? '#2E7D32' : '#757575',
                color: '#ffffff',
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        {!isEditing && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {!invoice ? (
        <DialogContent>
          <Alert severity="error">No invoice data available.</Alert>
        </DialogContent>
      ) : isEditing ? (
        renderEditMode()
      ) : (
        renderViewMode()
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isEditing ? (
          <>
            <Button onClick={handleCancelEdit} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={() => formik.handleSubmit()}
              variant="contained"
              disabled={saving || !formik.isValid}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} color="secondary" variant="outlined">
              Close
            </Button>
            <Button
              onClick={() => setIsEditing(true)}
              variant="contained"
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RecurringInvoiceDetailDialog;
